import { forwardRef } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Avatar,
  Group,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useTranslation } from "react-i18next";

const useStyles = createStyles((theme) => ({
  icon: {
    color: theme.colors.gray[6],
  },
}));

interface UserButtonProps extends React.ComponentPropsWithoutRef<"button"> {
  email: string;
}

export const UserButton = forwardRef<HTMLButtonElement, UserButtonProps>(
  ({ email, ...rest }: UserButtonProps, ref) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const theme = useMantineTheme();
    const colorScheme = useShownColorScheme();

    return (
      <UnstyledButton
        ref={ref}
        sx={(theme, u) => ({
          [u.dark]: {
            color: theme.colors.gray[4],
          },
          [u.light]: {
            color: theme.black,
          },
          display: "block",
          width: "100%",
          padding: theme.spacing.xs,
          borderRadius: theme.radius.sm,
          "@media(hover: hover)": {
            "&:hover": {
              [u.dark]: {
                backgroundColor: theme.colors.dark[8],
              },
              [u.light]: {
                backgroundColor: theme.colors.gray[0],
              },
            },
          },
        })}
        {...rest}
      >
        <Group gap="xs" sx={{ flexWrap: "nowrap" }}>
          <Avatar
            variant="light"
            radius="xl"
            color={
              colorScheme === "dark"
                ? theme.colors.violet[2]
                : theme.colors.violet[7]
            }
          />
          <div style={{ flex: 1 }}>
            <Text size="sm" sx={{ fontWeight: 500 }}>
              {t("generic.your_account_label")}
            </Text>

            <Text c="dimmed" size="xs">
              {email}
            </Text>
          </div>

          <FontAwesomeIcon icon={faChevronDown} className={classes.icon} />
        </Group>
      </UnstyledButton>
    );
  }
);

UserButton.displayName = "UserButton";
